import React from 'react';
import { Image, Sparkles, Eye, Type, MessageSquare, Wand2 } from 'lucide-react';

// CTA options
export const CTA_OPTIONS = [
    { value: 'LEARN_MORE', label: 'Learn More' },
    { value: 'SHOP_NOW', label: 'Shop Now' },
    { value: 'SIGN_UP', label: 'Sign Up' },
    { value: 'BOOK_NOW', label: 'Book Now' },
    { value: 'CONTACT_US', label: 'Contact Us' },
    { value: 'DOWNLOAD', label: 'Download' },
    { value: 'GET_OFFER', label: 'Get Offer' },
    { value: 'GET_QUOTE', label: 'Get Quote' },
    { value: 'SUBSCRIBE', label: 'Subscribe' },
    { value: 'WATCH_MORE', label: 'Watch More' }
];

/**
 * AdForm - Reusable form component for Ad node editing
 * Used in both AdModal and PropertiesPanel
 */
const AdForm = ({
    formData,
    onChange,
    errors = {},
    onOpenCreativePicker,
    onGenerateWithAI,
    onGenerateCreative,
    isGenerating = false,
    funnelContext = null
}) => {
    const handleChange = (field, value) => {
        onChange(field, value);
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
            {/* Ad Name */}
            <div className="input-group">
                <label className="input-label">Ad Name *</label>
                <input
                    type="text"
                    className="input-field"
                    placeholder="e.g., Summer Video - Discount CTA"
                    value={formData.name || ''}
                    onChange={(e) => handleChange('name', e.target.value)}
                    style={errors.name ? { borderColor: 'var(--color-error)' } : {}}
                />
                {errors.name && (
                    <span style={{ color: 'var(--color-error)', fontSize: 'var(--font-size-xs)' }}>
                        {errors.name}
                    </span>
                )}
            </div>

            {/* Creative Selection */}
            <div className="input-group">
                <label className="input-label">Creative *</label>
                <div
                    onClick={onOpenCreativePicker}
                    style={{
                        border: errors.creative
                            ? '2px dashed var(--color-error)'
                            : '2px dashed var(--color-border)',
                        borderRadius: 'var(--radius-lg)',
                        padding: 'var(--space-4)',
                        cursor: 'pointer',
                        textAlign: 'center',
                        background: 'var(--color-bg-secondary)',
                        transition: 'all var(--transition-fast)'
                    }}
                >
                    {formData.creativeUrl || formData.creativeThumbnail ? (
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 'var(--space-3)'
                        }}>
                            <div style={{
                                width: '100px',
                                height: '100px',
                                borderRadius: 'var(--radius-md)',
                                overflow: 'hidden',
                                background: 'var(--color-bg-tertiary)'
                            }}>
                                <img
                                    src={formData.creativeThumbnail || formData.creativeUrl}
                                    alt="Selected creative"
                                    style={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'cover'
                                    }}
                                />
                            </div>
                            <div style={{ textAlign: 'left' }}>
                                <div style={{
                                    fontSize: 'var(--font-size-sm)',
                                    fontWeight: 600,
                                    color: 'var(--color-text-primary)'
                                }}>
                                    Creative Selected
                                </div>
                                <div style={{
                                    fontSize: 'var(--font-size-xs)',
                                    color: 'var(--color-text-tertiary)',
                                    marginTop: 'var(--space-1)'
                                }}>
                                    {formData.isVideo ? 'Video' : 'Image'} - Click to change
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div style={{ padding: 'var(--space-4)' }}>
                            <Image size={32} style={{ color: 'var(--color-text-tertiary)', marginBottom: 'var(--space-2)' }} />
                            <div style={{
                                fontSize: 'var(--font-size-sm)',
                                color: 'var(--color-text-secondary)'
                            }}>
                                Click to select from Asset Library
                            </div>
                        </div>
                    )}
                </div>
                {errors.creative && (
                    <span style={{ color: 'var(--color-error)', fontSize: 'var(--font-size-xs)' }}>
                        {errors.creative}
                    </span>
                )}
                {/* Generate New Creative Button */}
                {onGenerateCreative && (
                    <button
                        type="button"
                        onClick={onGenerateCreative}
                        className="btn btn-outline"
                        style={{
                            marginTop: 'var(--space-2)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 'var(--space-2)',
                            width: '100%',
                            padding: 'var(--space-3)',
                            border: '1px solid var(--color-primary)',
                            background: 'transparent',
                            color: 'var(--color-primary)',
                            borderRadius: 'var(--radius-md)',
                            cursor: 'pointer',
                            fontSize: 'var(--font-size-sm)',
                            fontWeight: 500,
                            transition: 'all var(--transition-fast)'
                        }}
                    >
                        <Wand2 size={16} />
                        Generate New Creative
                        {funnelContext?.funnelStage && (
                            <span style={{
                                fontSize: 'var(--font-size-xs)',
                                padding: '2px 6px',
                                borderRadius: 'var(--radius-sm)',
                                background: 'rgba(255,255,255,0.2)',
                                marginLeft: 'var(--space-1)'
                            }}>
                                {funnelContext.funnelStage}
                            </span>
                        )}
                    </button>
                )}
            </div>

            {/* AI Generate Button */}
            {onGenerateWithAI && (
                <button
                    type="button"
                    onClick={onGenerateWithAI}
                    disabled={isGenerating}
                    className="btn btn-secondary"
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 'var(--space-2)'
                    }}
                >
                    <Sparkles size={16} className={isGenerating ? 'animate-pulse' : ''} />
                    {isGenerating ? 'Generating...' : 'Generate Copy with AI'}
                </button>
            )}

            {/* Headline */}
            <div className="input-group">
                <label className="input-label">
                    <Type size={14} style={{ marginRight: 'var(--space-1)' }} />
                    Headline *
                </label>
                <input
                    type="text"
                    className="input-field"
                    placeholder="Catchy headline for your ad"
                    value={formData.headline || ''}
                    onChange={(e) => handleChange('headline', e.target.value)}
                    maxLength={40}
                    style={errors.headline ? { borderColor: 'var(--color-error)' } : {}}
                />
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontSize: 'var(--font-size-xs)',
                    color: 'var(--color-text-tertiary)',
                    marginTop: 'var(--space-1)'
                }}>
                    <span>{errors.headline || 'Keep it short and impactful'}</span>
                    <span>{(formData.headline || '').length}/40</span>
                </div>
            </div>

            {/* Primary Text */}
            <div className="input-group">
                <label className="input-label">
                    <MessageSquare size={14} style={{ marginRight: 'var(--space-1)' }} />
                    Primary Text
                </label>
                <textarea
                    className="input-field"
                    placeholder="Main body text for your ad..."
                    value={formData.primaryText || ''}
                    onChange={(e) => handleChange('primaryText', e.target.value)}
                    rows={3}
                    maxLength={125}
                    style={{ resize: 'vertical', minHeight: '80px' }}
                />
                <div style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    fontSize: 'var(--font-size-xs)',
                    color: 'var(--color-text-tertiary)',
                    marginTop: 'var(--space-1)'
                }}>
                    {(formData.primaryText || '').length}/125
                </div>
            </div>

            {/* Description */}
            <div className="input-group">
                <label className="input-label">Description (Optional)</label>
                <input
                    type="text"
                    className="input-field"
                    placeholder="Additional description"
                    value={formData.description || ''}
                    onChange={(e) => handleChange('description', e.target.value)}
                    maxLength={30}
                />
            </div>

            {/* CTA and URL */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 'var(--space-3)' }}>
                <div className="input-group">
                    <label className="input-label">Call to Action</label>
                    <select
                        className="select-field"
                        value={formData.cta || 'LEARN_MORE'}
                        onChange={(e) => handleChange('cta', e.target.value)}
                    >
                        {CTA_OPTIONS.map(cta => (
                            <option key={cta.value} value={cta.value}>
                                {cta.label}
                            </option>
                        ))}
                    </select>
                </div>
                <div className="input-group">
                    <label className="input-label">Destination URL</label>
                    <input
                        type="url"
                        className="input-field"
                        placeholder="https://yoursite.com/landing"
                        value={formData.destinationUrl || ''}
                        onChange={(e) => handleChange('destinationUrl', e.target.value)}
                    />
                </div>
            </div>

            {/* Preview Section */}
            {(formData.creativeUrl || formData.headline || formData.primaryText) && (
                <div className="input-group">
                    <label className="input-label">
                        <Eye size={14} style={{ marginRight: 'var(--space-1)' }} />
                        Preview
                    </label>
                    <div style={{
                        border: '1px solid var(--color-border)',
                        borderRadius: 'var(--radius-lg)',
                        overflow: 'hidden',
                        background: 'var(--color-bg-secondary)'
                    }}>
                        {/* Creative preview */}
                        {formData.creativeUrl && (
                            <div style={{
                                width: '100%',
                                height: '200px',
                                background: 'var(--color-bg-tertiary)'
                            }}>
                                <img
                                    src={formData.creativeThumbnail || formData.creativeUrl}
                                    alt="Ad preview"
                                    style={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'cover'
                                    }}
                                />
                            </div>
                        )}
                        {/* Text preview */}
                        <div style={{ padding: 'var(--space-3)' }}>
                            {formData.headline && (
                                <div style={{
                                    fontSize: 'var(--font-size-md)',
                                    fontWeight: 600,
                                    color: 'var(--color-text-primary)',
                                    marginBottom: 'var(--space-1)'
                                }}>
                                    {formData.headline}
                                </div>
                            )}
                            {formData.primaryText && (
                                <div style={{
                                    fontSize: 'var(--font-size-sm)',
                                    color: 'var(--color-text-secondary)',
                                    marginBottom: 'var(--space-2)'
                                }}>
                                    {formData.primaryText}
                                </div>
                            )}
                            {formData.cta && (
                                <button
                                    type="button"
                                    style={{
                                        background: 'var(--color-primary)',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: 'var(--radius-md)',
                                        padding: '8px 16px',
                                        fontSize: 'var(--font-size-sm)',
                                        fontWeight: 600,
                                        cursor: 'default'
                                    }}
                                >
                                    {CTA_OPTIONS.find(c => c.value === formData.cta)?.label || formData.cta}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdForm;
